import React, { ReactNode } from "react";
import { FormikProps } from "formik";
import ATMBreadCrumbs, {
  BreadcrumbType,
} from "src/components/UI/atoms/ATMBreadCrumbs/ATMBreadCrumbs";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
import ATMTextField from "src/components/UI/atoms/formFields/ATMTextField/ATMTextField";
import ATMSelect from "src/components/UI/atoms/formFields/ATMSelect/ATMSelect";
import { IoPersonCircleOutline } from "react-icons/io5";
import { RxDashboard } from "react-icons/rx";

export type DobProofMasterInitialValues = {
  name: string;
  documentRequired: boolean;
  numberOfDocumentsRequired: number;
  documentLabels: string[];
};

type Props = {
  formikProps: FormikProps<DobProofMasterInitialValues>;
};

const FormSectionHeading = ({ children }: { children: ReactNode }) => {
  return (
    <div className="border-l-[3px] border-primary-main px-3 py-2 text-slate-700 font-medium text-[15px] sticky top-0 bg-white z-50">
      {children}
    </div>
  );
};

const breadcrumbs: BreadcrumbType[] = [
  {
    label: "DOB Proof Master",
    path: "/dob-proof-master",
    icon: IoPersonCircleOutline,
  },
  {
    label: "Add DOB Proof Type",
    icon: RxDashboard,
  },
];

const documentRequiredOptions = [
  { label: "Yes", value: true },
  { label: "No", value: false },
];

const AddDobProofMaster = ({ formikProps }: Props) => {
  const { values, setFieldValue, isSubmitting } = formikProps;

  return (
    <div className="h-full py-5 px-4 flex flex-col gap-3">
      <ATMBreadCrumbs breadcrumbs={breadcrumbs} />
      <div className="shadow-sm flex flex-col overflow-auto border rounded p-3 bg-white relative">
        <div className="grow overflow-auto">
          <div className="pb-3">
            <FormSectionHeading>DOB PROOF TYPE INFORMATION</FormSectionHeading>

            <div className="px-3 py-5 grid grid-cols-3 gap-5">
              {/* Name */}
              <div>
                <ATMTextField
                  name="name"
                  value={values.name}
                  onChange={(e) => {
                    setFieldValue("name", e.target.value);
                  }}
                  label="Name"
                  placeholder="Enter DOB Proof Type Name"
                />
              </div>

              {/* Document Required */}
              <div>
                <ATMSelect
                  name="documentRequired"
                  value={values.documentRequired}
                  onChange={(e) => {
                    const isRequired = e.target.value === "true";
                    setFieldValue("documentRequired", isRequired);
                    if (!isRequired) {
                      setFieldValue("numberOfDocumentsRequired", 0);
                    } else if (values.numberOfDocumentsRequired < 1) {
                      setFieldValue("numberOfDocumentsRequired", 1);
                    }
                  }}
                  label="Document Required"
                  options={documentRequiredOptions.map((opt) => ({
                    label: opt.label,
                    value: String(opt.value),
                  }))}
                />
              </div>

              {/* Number of Documents Required */}
              {values.documentRequired && (
                <div>
                  <ATMTextField
                    name="numberOfDocumentsRequired"
                    value={String(values.numberOfDocumentsRequired)}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 0;
                      const count = val < 1 ? 1 : val;
                      setFieldValue("numberOfDocumentsRequired", count);
                      // Resize documentLabels array to match count
                      const currentLabels = [...(values.documentLabels || [])];
                      while (currentLabels.length < count) {
                        currentLabels.push("");
                      }
                      setFieldValue("documentLabels", currentLabels.slice(0, count));
                    }}
                    label="Number of Attachments Required"
                    placeholder="e.g. 1, 2, 3"
                  />
                </div>
              )}
            </div>

            {/* Document Labels */}
            {values.documentRequired && values.numberOfDocumentsRequired > 0 && (
              <div className="px-3 pb-5">
                <FormSectionHeading>DOCUMENT LABELS</FormSectionHeading>
                <div className="grid grid-cols-3 gap-5 pt-3">
                  {Array.from({ length: values.numberOfDocumentsRequired }, (_, index) => (
                    <div key={index}>
                      <ATMTextField
                        name={`documentLabels[${index}]`}
                        value={(values.documentLabels || [])[index] || ""}
                        onChange={(e) => {
                          const updatedLabels = [...(values.documentLabels || [])];
                          while (updatedLabels.length <= index) {
                            updatedLabels.push("");
                          }
                          updatedLabels[index] = e.target.value;
                          setFieldValue("documentLabels", updatedLabels);
                        }}
                        label={`Document ${index + 1} Label`}
                        placeholder={`e.g. Front Side, Back Side`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end">
          <div>
            <ATMLoadingButton type="submit" isLoading={isSubmitting}>
              Submit
            </ATMLoadingButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddDobProofMaster;
